// ============================================================
// Contrack AI — Step 0: cinematic intro sequence
// Single choreographed timeline. Click anywhere to skip to welcome.
// Faithful port of the prototype's intro.jsx (incl. the slowed
// typography timing the user asked for).
// ============================================================
import { useState, useEffect, useRef } from "react";
import { Logo, IconCheck, IconArrow } from "./data";
import { useOnboardingLang, fmtAmount } from "./i18n";

// Abstract invoice line items for the "magic trick" phase.
// Amounts are raw numbers, formatted per language/currency at render.
const SCAN_LINES = [
  { w: 58, value: 84200, status: "ok" },
  { w: 44, value: 12800, status: "ok" },
  { w: 70, value: 156400, status: "ok" },
  { w: 52, value: 42500, status: "flag" }, // the catch
  { w: 38, value: 9900, status: "ok" },
];

type Stage = "logo" | "type" | "invoice" | "welcome";

export default function IntroSequence({ onDone }: { onDone: () => void }) {
  const { copy, lang } = useOnboardingLang();
  const INTRO_WORDS = copy.introWords;
  const [stage, setStage] = useState<Stage>("logo");
  const [wordIdx, setWordIdx] = useState(0);
  const [periodGreen, setPeriodGreen] = useState(false);
  const [resolved, setResolved] = useState(0);   // how many scan lines have resolved
  const [scanGo, setScanGo] = useState(false);    // start the scan-line sweep
  const [ready, setReady] = useState(false);      // welcome content visible
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  const skipped = useRef(false);

  function clearTimers() {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  }

  function skip() {
    if (skipped.current) return;
    skipped.current = true;
    clearTimers();
    setStage("welcome");
    setResolved(SCAN_LINES.length);
    setPeriodGreen(true);
    setReady(true);
  }

  useEffect(() => {
    const at = (ms: number, fn: () => void) => timers.current.push(setTimeout(fn, ms));
    // PHASE 1 — logo assembly (handled by CSS on mount); hold to 1.5s
    // PHASE 2 — kinetic typography (slower so each line is readable)
    at(1500, () => { setStage("type"); setWordIdx(0); });
    at(2550, () => setWordIdx(1));
    at(3600, () => setWordIdx(2));
    at(4150, () => setPeriodGreen(true));
    // PHASE 3 — the magic trick (final word holds ~1.3s before this)
    at(4900, () => { setStage("invoice"); });
    at(5150, () => setScanGo(true));
    at(5400, () => setResolved(1));
    at(5750, () => setResolved(2));
    at(6100, () => setResolved(3));
    at(6500, () => setResolved(4));   // line 4 = the red catch (climax)
    at(7150, () => setResolved(5));   // 650ms beat, then line 5 resolves
    // PHASE 4 — welcome
    at(7800, () => { setStage("welcome"); });
    at(7960, () => setReady(true));
    return clearTimers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showSkip = stage !== "welcome";

  return (
    <div className="intro" onClick={showSkip ? skip : undefined}>
      <div className="intro-radial" />

      {showSkip && <div className="intro-skip-hint">{copy.skipHint}</div>}

      {/* PHASE 1 + 2 share the centered logo/type column */}
      {(stage === "logo" || stage === "type") && (
        <div className="intro-center">
          <div className={"intro-logo" + (stage === "type" ? " intro-logo-out" : "")}>
            <Logo scale={3} mode="assemble" />
          </div>
          {stage === "type" && (
            <div className="intro-type">
              <span key={wordIdx} className="intro-word">
                {INTRO_WORDS[wordIdx].replace(/\.$/, "")}
                <span className={"intro-period" + (wordIdx === 2 && periodGreen ? " intro-period-green" : "")}>.</span>
              </span>
            </div>
          )}
        </div>
      )}

      {/* PHASE 3 — invoice scan */}
      {stage === "invoice" && (
        <div className="intro-center">
          <div className="scan-card">
            <div className="scan-card-head">
              <span className="scan-doc-title" />
              <span className="scan-doc-meta" />
            </div>
            {scanGo && <div className="scan-line-sweep" />}
            <div className="scan-rows">
              {SCAN_LINES.map((ln, i) => {
                const done = i < resolved;
                const flagged = ln.status === "flag" && done;
                return (
                  <div key={i} className={"scan-row" + (flagged ? " scan-row-flag" : "")}>
                    <span className="scan-status">
                      {done && ln.status === "ok" && <span className="scan-dot scan-dot-ok"><IconCheck size={11} style={{ color: "#fff" }} /></span>}
                      {done && ln.status === "flag" && <span className="scan-dot scan-dot-flag" />}
                    </span>
                    <span className="scan-bar" style={{ width: ln.w + "%" }} />
                    <span className="scan-amount">{fmtAmount(ln.value, lang)}</span>
                    {flagged && <span className="scan-pill">{copy.discrepancyFound(fmtAmount(42500, lang))}</span>}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* PHASE 4 — welcome */}
      {stage === "welcome" && (
        <div className={"intro-welcome" + (ready ? " is-ready" : "")}>
          <div className="intro-welcome-logo"><Logo scale={1.1} mode="static" /></div>
          <h1 className="intro-welcome-title">{copy.welcomeTitle}</h1>
          <p className="intro-welcome-sub">{copy.welcomeSub}</p>
          <button className="btn-primary intro-cta" type="button" onClick={onDone}>
            {copy.cta} <IconArrow size={18} style={{ marginLeft: 2 }} />
          </button>
        </div>
      )}
    </div>
  );
}
